import React, { useEffect, useState, useMemo } from 'react';
import { getFirestore, collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import StatisticsScreen from './StatisticsScreen';

const HistoryScreen = ({ onBack, onHome, onLogout }) => {
    const [gameHistory, setGameHistory] = useState([]);
    const [selectedGame, setSelectedGame] = useState(null);
    const appId = "1:699575750232:web:8d7ebbfd7a7bb5c866842d";

    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    useEffect(() => {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
            fetchGames();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const fetchGames = async () => {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user || loading) return;

        setLoading(true);
        const db = getFirestore();
        const gamesRef = collection(db, "personalData", appId, "users", user.uid, "games");

        try {
            // Fetch up to 1000 games at once to avoid pagination issues with heavy filtering
            const q = query(gamesRef, orderBy("date", "desc"), limit(1000));
            const querySnapshot = await getDocs(q);
            const fetchedGames = querySnapshot.docs.map(doc => doc.data());



            const validGames = fetchedGames.filter(game => {
                // Show any game that has at least one hand recorded
                if (game.handHistory && game.handHistory.length > 0) return true;

                // Also show if it has a winner (legacy support)
                if (game.winner || (game.winners && game.winners.length > 0)) return true;

                return false;
            });


            setGameHistory(validGames);
            setHasMore(false); // Disable load more since we fetched a large batch
        } catch (error) {
            console.error("Error fetching history:", error);
        } finally {
            setLoading(false);
        }
    };

    const stats = useMemo(() => {
        if (gameHistory.length === 0) return null;

        const playerWins = {};
        let totalScore = 0;
        let totalHands = 0;
        let highestScore = 0;

        gameHistory.forEach(game => {
            if (game.handHistory) {
                totalHands += game.handHistory.length;
            }

            // Only count wins for finished games
            if (game.winner || (game.winners && game.winners.length > 0)) {
                // Handle ties (multiple winners)
                if (game.winners && game.winners.length > 0) {
                    game.winners.forEach(winner => {
                        if (winner.name) {
                            playerWins[winner.name] = (playerWins[winner.name] || 0) + 1;
                        }
                    });
                }
                // Fallback for legacy games (single winner)
                else if (game.winner && game.winner.name) {
                    const winner = game.winner.name;
                    playerWins[winner] = (playerWins[winner] || 0) + 1;
                }
            }


            if (game.players) {
                game.players.forEach(p => {
                    totalScore += p.score;
                    if (p.score > highestScore) highestScore = p.score;
                });
            }
        });

        const topPlayer = Object.entries(playerWins).reduce((a, b) => a[1] > b[1] ? a : b, ['', 0]);
        const totalGames = gameHistory.length;

        return {
            totalGames,
            totalHands,
            topPlayer: topPlayer[0],
            topPlayerWins: topPlayer[1],
            topPlayerWinRate: Math.round((topPlayer[1] / totalGames) * 100),
            avgScore: Math.round(totalScore / (totalGames * gameHistory[0].players.length)),
            highestScore,
        };
    }, [gameHistory]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();

        // Reset times to compare calendar days
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const gameDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const diffTime = today - gameDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;

        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getGameTags = (game) => {
        const tags = [];
        if (!game.players || game.players.length === 0) return tags;

        const sortedPlayers = [...game.players].sort((a, b) => b.score - a.score);
        const winner = sortedPlayers[0];
        const runnerUp = sortedPlayers[1];
        const lastPlace = sortedPlayers[sortedPlayers.length - 1];

        // Blowout: Winner won by > 100 points
        if (winner.score - runnerUp.score > 100) {
            tags.push({ label: 'Blowout', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: '💥' });
        }

        // Nailbiter: Winner won by < 10 points
        if (winner.score - runnerUp.score < 10) {
            tags.push({ label: 'Nailbiter', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: '😬' });
        }

        // Marathon: > 15 hands
        if (game.handHistory && game.handHistory.length > 15) {
            tags.push({ label: 'Marathon', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: '🏃' });
        }

        // Speed Run: < 8 hands (and finished)
        if (game.gameState === 'finished' && game.handHistory && game.handHistory.length < 8) {
            tags.push({ label: 'Speed Run', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: '⚡' });
        }

        // High Stakes: Total bags > 10
        const totalBags = game.players.reduce((sum, p) => sum + (p.bags || 0), 0);
        if (totalBags > 10) {
            tags.push({ label: 'Bag Fest', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: '💰' });
        }

        return tags;
    };

    // If a game is selected, show its statistics
    if (selectedGame) {
        return (
            <StatisticsScreen
                initialPlayers={selectedGame.players}
                initialHandHistory={selectedGame.handHistory}
                gameConfig={{ tricksPerHand: 13 }}
                gameState="finished"
                onClose={() => setSelectedGame(null)}
            />
        );
    }

    return (
        <div className="h-screen bg-[#1a2332] text-white font-['Inter'] flex flex-col items-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-full"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}
                />
            </div>

            {/* Ambient Glow */}
            <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

            <header className="relative z-20 flex items-center justify-between w-full max-w-5xl p-4 sm:p-6">
                <button
                    onClick={onBack}
                    className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                    title="Back"
                >
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400">
                    GAME HISTORY
                </h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onHome}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Home"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /></svg>
                    </button>
                    <button
                        onClick={onLogout}
                        className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                        title="Logout"
                    >
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    </button>
                </div>
            </header>

            <div className="relative z-10 w-full max-w-5xl flex-1 overflow-hidden px-4 pb-6 flex flex-col">
                {gameHistory.length === 0 && !loading ? (
                    <div className="h-full bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl flex flex-col items-center justify-center text-gray-400 p-8 text-center">
                        <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mb-6 animate-pulse">
                            <span className="text-6xl">🃏</span>
                        </div>
                        <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400 mb-3 font-['Roboto_Slab']">
                            No Games Played Yet
                        </h3>
                        <p className="text-gray-400 max-w-xs mx-auto mb-8 leading-relaxed">
                            Your hall of fame is waiting! Start your first game to begin tracking stats and history.
                        </p>
                        <button
                            onClick={onHome}
                            className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-2xl shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105 transition-all duration-200 flex items-center gap-2"
                        >
                            <span>Start New Game</span>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </button>
                    </div>
                ) : (
                    <div className="h-full flex flex-col gap-4 overflow-hidden">
                        {/* Stats Grid */}
                        {stats && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 flex-shrink-0">
                                <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-xl border border-white/10 p-3 hover:border-amber-500/30 transition-all">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Games</p>
                                        <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                            <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <p className="text-2xl font-black text-white leading-none">{stats.totalGames}</p>
                                    <p className="text-[10px] text-gray-500 mt-1 font-medium">{stats.totalHands} hands played</p>
                                </div>
                                <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-xl border border-white/10 p-3 hover:border-amber-500/30 transition-all">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Top Player</p>
                                        <span className="text-xs">👑</span>
                                    </div>
                                    <p className="text-base font-black text-amber-400 truncate leading-tight">{stats.topPlayer}</p>
                                    <p className="text-[10px] text-gray-500 mt-1 font-medium">{stats.topPlayerWins} wins ({stats.topPlayerWinRate}%)</p>
                                </div>
                                <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-xl border border-white/10 p-3 hover:border-amber-500/30 transition-all">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Avg Score</p>
                                        <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <p className="text-2xl font-black text-white leading-none">{stats.avgScore}</p>
                                    <p className="text-[10px] text-gray-500 mt-1 font-medium">Best: {stats.highestScore}</p>
                                </div>
                                <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-xl border border-white/10 p-3 hover:border-amber-500/30 transition-all">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Recent</p>
                                        <svg className="w-3 h-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <p className="text-xs font-bold text-white truncate leading-tight">{gameHistory[0] ? formatDate(gameHistory[0].date) : '-'}</p>
                                    <p className="text-[10px] text-gray-500 mt-1 font-medium truncate">
                                        {gameHistory[0]?.winner?.name ? `Won by ${gameHistory[0].winner.name}` : 'No winner'}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Timeline */}
                        <div className="flex-1 bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
                            <div className="flex-1 overflow-y-auto p-3 sm:p-6 pb-12 sm:pb-12 custom-scrollbar">
                                <div className="relative">
                                    {/* Timeline Line */}
                                    <div className="absolute left-3.5 sm:left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500/50 via-amber-500/20 to-transparent" />

                                    <div className="space-y-4 sm:space-y-6">
                                        {gameHistory.map((game, index) => {
                                            const sortedPlayers = [...game.players].sort((a, b) => b.score - a.score);
                                            const winner = sortedPlayers[0];
                                            const isTie = game.winners && game.winners.length > 1;
                                            const tags = getGameTags(game);
                                            const isRecent = index < 3;

                                            // Determine game status/winner display
                                            let winnerDisplay = 'Unknown';
                                            let statusColor = 'text-gray-400';
                                            let statusBg = 'bg-gray-500/10 border-gray-500/20';

                                            if (isTie) {
                                                winnerDisplay = 'Tie Game';
                                                statusColor = 'text-amber-400';
                                                statusBg = 'bg-amber-500/10 border-amber-500/20';
                                            } else if (game.winner?.name) {
                                                winnerDisplay = game.winner.name;
                                                statusColor = 'text-amber-400';
                                                statusBg = 'bg-amber-500/10 border-amber-500/20';
                                            } else if (game.gameState === 'playing' || (game.handHistory && game.handHistory.length < 13)) {
                                                winnerDisplay = 'In Progress';
                                                statusColor = 'text-blue-400';
                                                statusBg = 'bg-blue-500/10 border-blue-500/20';
                                            }

                                            return (
                                                <div key={index} className="relative pl-10 sm:pl-16 group">
                                                    {/* Timeline Dot */}
                                                    <div className={`absolute left-1.5 sm:left-4 top-4 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-[3px] sm:border-4 border-[#1e293b] ${isRecent ? 'bg-amber-500 shadow-lg shadow-amber-500/50' : 'bg-gray-600'} group-hover:scale-125 transition-transform z-10`} />

                                                    <div
                                                        onClick={() => setSelectedGame(game)}
                                                        className="group relative bg-black/20 rounded-2xl border border-white/5 overflow-hidden hover:border-amber-500/30 hover:shadow-lg hover:shadow-amber-500/10 transition-all cursor-pointer"
                                                    >
                                                        {/* Card Header */}
                                                        <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3 border-b border-white/5 bg-gradient-to-r from-white/5 to-transparent">
                                                            <div className="flex flex-row sm:flex-col items-center sm:items-start justify-between gap-1">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-base sm:text-lg font-black text-white font-['Roboto_Slab']">
                                                                        {formatDate(game.date)}
                                                                    </span>
                                                                    <span className="text-[10px] sm:text-xs font-bold text-gray-500 bg-black/20 px-2 py-0.5 rounded-full">
                                                                        {getTimeAgo(game.date)}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-2 text-[10px] sm:text-xs font-bold text-gray-400">
                                                                    <span>{game.players.length} Players</span>
                                                                    <span>•</span>
                                                                    <span>{game.handHistory ? game.handHistory.length : 0} Hands</span>
                                                                </div>
                                                            </div>

                                                            {/* Tags */}
                                                            <div className="flex flex-wrap sm:justify-end gap-1.5 sm:gap-2 w-full sm:max-w-[60%]">
                                                                {tags.map((tag, tIdx) => (
                                                                    <span key={tIdx} className={`text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-lg border flex items-center gap-1 ${tag.color}`}>
                                                                        <span>{tag.icon}</span>
                                                                        <span className="hidden sm:inline">{tag.label}</span>
                                                                    </span>
                                                                ))}
                                                                <div className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-xl border flex items-center gap-1.5 ${statusBg} ml-auto sm:ml-0`}>
                                                                    <span className="text-[10px] sm:text-xs">{winnerDisplay === 'In Progress' ? '⏳' : '🏆'}</span>
                                                                    <span className={`text-[10px] sm:text-xs font-black uppercase tracking-wider ${statusColor}`}>
                                                                        {winnerDisplay}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Card Body */}
                                                        <div className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
                                                            {/* Winner Avatar (Mobile & Desktop) */}
                                                            <div className="flex items-center gap-3 w-full sm:w-auto sm:flex-col sm:min-w-[70px]">
                                                                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-amber-300 to-orange-500 p-0.5 shadow-lg shadow-amber-500/20 flex-shrink-0">
                                                                    <div className="w-full h-full bg-[#1e293b] rounded-[10px] sm:rounded-[14px] flex items-center justify-center relative overflow-hidden">
                                                                        <span className="text-sm sm:text-xl font-black text-white">
                                                                            {winner.name.substring(0, 2).toUpperCase()}
                                                                        </span>
                                                                        <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/20 to-transparent pointer-events-none" />
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-col sm:items-center">
                                                                    <span className="text-[9px] font-black text-amber-400 uppercase tracking-wider">Winner</span>
                                                                    <span className="text-sm font-bold text-white sm:hidden">{winner.name}</span>
                                                                </div>
                                                            </div>

                                                            {/* Score List */}
                                                            <div className="w-full grid grid-cols-2 gap-2">
                                                                {sortedPlayers.map((player, pIdx) => (
                                                                    <div key={pIdx} className="relative bg-black/20 rounded-xl p-2 border border-white/5 flex flex-col justify-between group-hover:bg-white/5 transition-colors">
                                                                        <div className="flex items-center justify-between mb-1">
                                                                            <span className={`text-[9px] font-black uppercase tracking-wider ${pIdx === 0 ? 'text-amber-400' : 'text-gray-500'}`}>
                                                                                {pIdx === 0 ? '1st' : pIdx === 1 ? '2nd' : pIdx === 2 ? '3rd' : `${pIdx + 1}th`}
                                                                            </span>
                                                                            {player.bags > 0 && (
                                                                                <span className="text-[9px] text-red-400 font-bold flex items-center gap-0.5">
                                                                                    {player.bags} 💼
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <div className="font-bold text-xs truncate" style={{ color: player.color }}>
                                                                                {player.name}
                                                                            </div>
                                                                            <div className="text-lg font-black text-white font-mono leading-none mt-0.5">
                                                                                {player.score}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Arrow */}
                                                            <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-white/5 text-gray-400 group-hover:bg-amber-500 group-hover:text-[#1e293b] transition-all transform group-hover:translate-x-1">
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HistoryScreen;
